import { unwrap, type Schemas } from '@mrigl/api-client';
import type { Locale } from '@mrigl/shared';
import { Alert, Card } from '@mrigl/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore } from '../catalog/shared.js';

type Billing = Schemas['StoreBilling'];
type Status = Billing['status'];

/** One sentence for the subscription state (dates in Tunis time). */
export function statusText(
  t: I18n['t'],
  formatDateTime: (value: string) => string,
  status: Status,
): string {
  const date = (value: string | null) => (value === null ? '' : formatDateTime(value));
  switch (status.state) {
    case 'trialing':
      return t('admin.billing.state.trialing', { date: date(status.trialEndsAt) });
    case 'active':
      return t('admin.billing.state.active', { date: date(status.paidUntil) });
    case 'grace':
      return t('admin.billing.state.grace', { date: date(status.graceEndsAt) });
    default:
      return t(`admin.billing.state.${status.state}`);
  }
}

/** Plan name in the admin language (fallbacks as for every localized text). */
export function planName(name: Record<string, string>, locale: Locale, code: string): string {
  return name[locale] ?? name['fr'] ?? name['ar'] ?? Object.values(name)[0] ?? code;
}

/** The store's subscription: plan, state, usage against limits and mrigl's invoices. */
export function BillingPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatPrice, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [billing, setBilling] = useState<Billing | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/billing', { params: { path: { storeId } } }),
    )
      .then(setBilling)
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  const failed = storeError ?? error;
  if (failed !== null) return <Alert tone="error">{failed}</Alert>;
  if (store === null || billing === null) return <Loading />;
  const plan = billing.plan;
  const warning = billing.status.state === 'grace' || billing.status.state === 'restricted';
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.billing.title')}</h1>
      {plan === null ? (
        <p>{t('admin.billing.none')}</p>
      ) : (
        <Card labelledBy="billing-plan">
          <h2 id="billing-plan" className="mrigl-subheading" dir="auto">
            {t('admin.billing.plan', { name: planName(plan.name, locale, plan.code) })}
          </h2>
          {plan.price.minor === '0' ? null : (
            <p>
              {t('admin.billing.price', {
                amount: formatPrice(plan.price.minor),
                months: plan.periodMonths,
              })}
            </p>
          )}
          {warning ? (
            <Alert tone="error">{statusText(t, formatDateTime, billing.status)}</Alert>
          ) : (
            <p>{statusText(t, formatDateTime, billing.status)}</p>
          )}
          <p className="mrigl-muted">{t('admin.billing.howToPay')}</p>
          <PaymentInstructions text={billing.paymentInstructions} />
        </Card>
      )}
      <Card labelledBy="billing-usage">
        <h2 id="billing-usage" className="mrigl-subheading">
          {t('admin.billing.usage')}
        </h2>
        <ul className="mrigl-list">
          {billing.usage.map((item) => {
            const kind = t(`admin.billing.kind.${item.kind}`);
            return (
              <li key={item.kind}>
                {item.limit === null
                  ? t('admin.billing.usageUnlimited', { kind, used: String(item.used) })
                  : t('admin.billing.usageLine', {
                      kind,
                      used: String(item.used),
                      limit: String(item.limit),
                    })}
              </li>
            );
          })}
        </ul>
      </Card>
      <Card labelledBy="billing-invoices">
        <h2 id="billing-invoices" className="mrigl-subheading">
          {t('admin.billing.invoices')}
        </h2>
        {billing.invoices.length === 0 ? (
          <p className="mrigl-muted">{t('admin.billing.noInvoices')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.order.invoice')}</th>
                  <th scope="col">{t('admin.order.column.date')}</th>
                  <th scope="col">{t('admin.operator.plan.period')}</th>
                  <th scope="col" className="admin-num">
                    {t('admin.order.column.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map((invoice) => (
                  <tr key={invoice.invoiceId}>
                    <td>
                      <a
                        className="admin-invoice-link"
                        href={`/admin-api/v1/stores/${storeId}/billing/invoices/${invoice.invoiceId}/pdf`}
                        download
                        aria-label={t('admin.billing.invoice', {
                          number: invoice.displayNumber,
                          date: formatDateTime(invoice.issuedAt),
                          amount: formatPrice(invoice.total.minor),
                        })}
                      >
                        <span className="admin-code mrigl-ltr">{invoice.displayNumber}</span>
                        <span className="admin-invoice-link__action">{t('admin.download')}</span>
                      </a>
                    </td>
                    <td className="admin-nowrap">{formatDateTime(invoice.issuedAt)}</td>
                    <td className="admin-nowrap">
                      {formatDateTime(invoice.periodFrom)} – {formatDateTime(invoice.periodTo)}
                    </td>
                    <td className="admin-num">{formatPrice(invoice.total.minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Warning on the store page when the subscription is overdue or restricted (owners and admins
 * only: others cannot read billing and see nothing).
 */
/** How to pay mrigl, as entered by operators (in the merchant's language when available). */
export function PaymentInstructions({ text }: { readonly text: Partial<Record<Locale, string>> }) {
  const { t, locale } = useI18n();
  const value = text[locale] ?? text.fr ?? text.ar ?? Object.values(text)[0];
  if (value === undefined || value.trim() === '') return null;
  return (
    <div className="mrigl-stack">
      <h3 className="mrigl-subheading">{t('admin.billing.paymentInstructions')}</h3>
      <p className="admin-prewrap" dir="auto">
        {value}
      </p>
    </div>
  );
}

export function BillingBanner({ storeId }: { readonly storeId: string }) {
  const { t, formatDateTime } = useI18n();
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/billing', { params: { path: { storeId } } }),
    )
      .then((result) => {
        setStatus(result.status);
      })
      .catch(() => undefined);
  }, [storeId]);

  if (status === null || (status.state !== 'grace' && status.state !== 'restricted')) return null;
  return (
    <Alert tone="error">
      {statusText(t, formatDateTime, status)}{' '}
      <Link to={`/stores/${storeId}/billing`}>{t('admin.billing.details')}</Link>
    </Alert>
  );
}
